import { sectionTypeMatches, useAgentDefinitions, usePlugins } from '@tunasong/plugin-lib'
import type { BlockAction } from '@tunasong/plugin-lib'
import { useThunkDispatch } from '@tunasong/redux'
import type { BlockRef } from '@tunasong/schemas'
import { openSidebarAgentWithFocus } from './open-sidebar-agent-with-focus.js'
import { useAgentChildren } from './use-agent-children.js'

/**
 * Aggregate per-block affordances for a given `BlockRef`. Combines:
 *
 *  - agent-contributed `AgentDefinition.blockAffordances` for every agent
 *    currently attached to the host entity (matched by `agent.definitionId`),
 *    filtered by `blockTypes` (with `'*'` meaning any kind); affordances with
 *    `sectionTypes` are additionally gated by `block.sectionType` (fail-open
 *    via `sectionTypeMatches`);
 *  - generic `TunaPlugin.getBlockActions(block)` contributions from every
 *    registered plugin (e.g., chord plugin's own "Transpose").
 *
 * Agent items dispatch `openSidebarAgentWithFocus` on select — surfacing the
 * Agents sidebar focused on the chosen agent with the targeted block preset
 * as the composer's focus and an optional draft pre-filled from
 * `buildAsk(block).messageTemplate`. Plugin items use their own `onSelect`.
 *
 * Pure read hook: no side effects on mount, no fetches. Re-renders when the
 * underlying graph cache, plugin set, or agent definitions change.
 */
export const useBlockActions = (block: BlockRef): BlockAction[] => {
  const attachedAgents = useAgentChildren(block.entityId)
  const agentDefs = useAgentDefinitions()
  const plugins = usePlugins('all')
  const dispatch = useThunkDispatch()

  const fromAgents = attachedAgents.flatMap<BlockAction>(agent => {
    const definitionId = agent.definitionId
    if (definitionId === undefined) {
      return []
    }
    const def = agentDefs.find(d => d.id === definitionId)
    const affordances = def?.blockAffordances ?? []
    return affordances
      .filter(a => a.blockTypes === '*' || a.blockTypes.includes(block.type))
      .filter(a => sectionTypeMatches(a, block))
      .map(a => ({
        id: `${agent.id}/${a.id}`,
        label: a.label,
        iconKey: a.iconKey,
        onSelect: () => {
          const { messageTemplate, focus } = a.buildAsk(block)
          dispatch(openSidebarAgentWithFocus({ agentId: agent.id, focus, draft: messageTemplate }))
        },
      }))
  })

  const fromPlugins = (plugins ?? []).flatMap(p => p.getBlockActions?.(block) ?? [])

  return [...fromAgents, ...fromPlugins]
}
